// Ítem de Mercado Libre en Mi marca, con las mismas piezas que Instagram y Tiendanube:
//   • Sin conectar → "Conectar" + modal que explica qué va a poder hacer Atlas.
//   • Conectada → "usuario · Desconectar" (modal de confirmación).
//   • Hay que reconectar → "Mercado Libre pidió reconectar · Reconectar".
// El estado lo trae `estadoDeMercadoLibre` (liviano: el backend lee su base, no Mercado
// Libre). Si el backend tiene la integración apagada (404), no se dibuja nada.
// Todo Mercado Libre vive en atlas-backend (ADR-2026-09-28-mercadolibre-vive-en-el-backend).

import React from "react";
import { createPortal } from "react-dom";
import { ChevronRight, Loader2, X } from "lucide-react";
import {
  abrirVentanaDeMercadoLibre,
  conectarMercadoLibreEnVentana,
  desconectarMercadoLibre,
  ErrorDeMercadoLibre,
  estadoDeMercadoLibre,
  type EstadoDeMercadoLibre,
} from "../lib/mercadoLibreConexion";
import { MercadoLibreIcon } from "./MercadoLibreIcon";
import { MercadoLibreConnectModal } from "./MercadoLibreConnectModal";
import { ChipDeIntegracion } from "./ChipDeIntegracion";

const ICONO = "h-[18px] w-[18px]";

export const MercadoLibreStatus: React.FC<{
  companyId: string | null;
  brandId: string | null;
  /** "chip" (default, cabecera de Mi marca: chip en escritorio y renglón de conexiones en
   *  mobile — ADR-2026-10-05-mi-marca-conexiones-en-un-renglon) o "boton" (la fila vieja
   *  de mobile). Misma lógica, distinto disparador. Era "card" (la caja de la columna
   *  izquierda de escritorio), que se fue con la cabecera de identidad. */
  variant?: "chip" | "boton";
  /** Modal de conectar controlado desde afuera (deep-link `?conectar=mercadolibre`). */
  connectOpen?: boolean;
  onConnectOpenChange?: (abierto: boolean) => void;
}> = ({ companyId, brandId, variant = "chip", connectOpen, onConnectOpenChange }) => {
  const [estado, setEstado] = React.useState<EstadoDeMercadoLibre | null>(null);
  const [mostrarConectarInterno, setMostrarConectarInterno] = React.useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = React.useState(false);
  const [conectando, setConectando] = React.useState(false);
  const [desconectando, setDesconectando] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // La marca de AHORA: lo que vuelve de una acción empezada con otra marca no se muestra.
  const marcaActualRef = React.useRef(brandId);
  marcaActualRef.current = brandId;

  const controlado = connectOpen !== undefined;
  const mostrarConectar = controlado ? !!connectOpen : mostrarConectarInterno;
  const setMostrarConectar = React.useCallback(
    (v: boolean) => {
      if (controlado) onConnectOpenChange?.(v);
      else setMostrarConectarInterno(v);
    },
    [controlado, onConnectOpenChange],
  );

  React.useEffect(() => {
    let cancelado = false;
    // Al cambiar de marca se olvida TODO lo de la anterior antes de preguntar: si no,
    // durante la consulta se vería su usuario y "Desconectar" borraría la conexión de la
    // marca NUEVA (mismo caso que la review de Greptile #140 en Instagram).
    setEstado(null);
    setMostrarConectarInterno(false);
    setMostrarConfirmar(false);
    setError(null);
    if (!companyId) return;
    void estadoDeMercadoLibre(brandId).then((s) => {
      if (!cancelado) setEstado(s);
    });
    return () => {
      cancelado = true;
    };
  }, [companyId, brandId]);

  // El deep-link abre "Conectar" sin saber el estado: si ya está conectada, no hay nada que abrir.
  React.useEffect(() => {
    if (controlado && connectOpen && estado?.conectada) onConnectOpenChange?.(false);
  }, [controlado, connectOpen, estado, onConnectOpenChange]);

  // Cargando, sin empresa, apagado en el backend o no pudimos preguntar: mejor no mostrar
  // nada que mostrar "sin conectar" a quien sí lo tiene conectado.
  if (!estado || estado.error || !estado.disponible) return null;

  const reconectar = !estado.conectada && estado.estado === "reconectar";
  const usuario = estado.nickname ?? "Conectada";

  const conectar = async () => {
    if (conectando) return;
    // ⚠️ La ventana se abre ACÁ, sincrónicamente dentro del click, ANTES de cualquier
    // `await`: Safari y los navegadores de iPhone la bloquean si no
    // (ADR-2026-08-07-tiendanube-popup-safari). No mover esta línea.
    const popup = abrirVentanaDeMercadoLibre();
    setConectando(true);
    setError(null);
    try {
      const marca = brandId;
      await conectarMercadoLibreEnVentana(marca, popup);
      const nuevo = await estadoDeMercadoLibre(marca);
      if (marcaActualRef.current !== marca) return; // cambió de marca mientras conectaba
      setEstado(nuevo);
      setMostrarConectar(false);
    } catch (e) {
      // Cerrar la ventana a propósito no es un error que haya que cantarle.
      if (e instanceof ErrorDeMercadoLibre && e.code === "ventana_cerrada") setError(null);
      else setError(e instanceof Error ? e.message : "No se pudo conectar.");
    } finally {
      setConectando(false);
    }
  };

  const confirmarDesconectar = async () => {
    if (desconectando) return;
    setDesconectando(true);
    setError(null);
    try {
      const marca = brandId;
      await desconectarMercadoLibre(marca);
      const nuevo = await estadoDeMercadoLibre(marca);
      if (marcaActualRef.current !== marca) return;
      setEstado(nuevo);
      setMostrarConfirmar(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo desconectar.");
    } finally {
      setDesconectando(false);
    }
  };

  const abrirConectar = () => {
    setError(null);
    setMostrarConectar(true);
  };

  const accion = estado.conectada ? (
    <button
      type="button"
      onClick={() => {
        setError(null);
        setMostrarConfirmar(true);
      }}
      className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
    >
      Desconectar
    </button>
  ) : (
    <button
      type="button"
      onClick={abrirConectar}
      className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-colors"
    >
      {reconectar ? "Reconectar" : "Conectar"}
    </button>
  );

  const subtitulo = estado.conectada ? usuario : reconectar ? "Mercado Libre pidió reconectar" : null;

  return (
    <>
      {variant === "chip" ? (
        // Chip de la cabecera de Mi marca — ADR-2026-10-02-mi-marca-cabecera-de-identidad.
        <ChipDeIntegracion
          clave="mercadolibre"
          icono={<MercadoLibreIcon className="h-4 w-4 shrink-0" />}
          nombre="Mercado Libre"
          estado={estado.conectada ? "conectada" : reconectar ? "vencida" : "desconectada"}
          detalle={estado.conectada ? usuario : null}
          onConectar={abrirConectar}
          onDesconectar={() => {
            setError(null);
            setMostrarConfirmar(true);
          }}
        />
      ) : !estado.conectada && !reconectar ? (
        // ── Fila completa (mobile), la misma caja que la de Tiendanube ──
        <button
          type="button"
          onClick={abrirConectar}
          className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.07] bg-white px-3 py-3 text-left transition-opacity active:opacity-70"
        >
          <MercadoLibreIcon className={ICONO} />
          <span className="flex-1 text-[14px] font-medium text-brand-black/85">Conectar Mercado Libre</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-black/30" />
        </button>
      ) : (
        <div className="flex w-full items-center justify-between gap-2 rounded-2xl border border-black/[0.07] bg-white px-3 py-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <MercadoLibreIcon className={ICONO} />
            <div className="min-w-0">
              <div className="text-[14px] font-medium text-brand-black/80 leading-tight">Mercado Libre</div>
              {subtitulo && (
                <div className="text-[12px] text-brand-black/45 truncate leading-tight">{subtitulo}</div>
              )}
            </div>
          </div>
          {accion}
        </div>
      )}

      {/* Importación masiva: arranca sola al conectar (ADR-2026-09-29-importacion-masiva-front). */}

      {/* Modal: conectar (qué va a poder hacer Atlas y qué no). Compartido con "Subir varios"
          (ADR-2026-10-22-subir-varios-conecta-con-la-misma-explicacion). */}
      <MercadoLibreConnectModal
        open={mostrarConectar}
        reconectar={reconectar}
        conectando={conectando}
        error={error}
        onCancel={() => setMostrarConectar(false)}
        onConnect={conectar}
      />

      {/* Modal: confirmar desconexión */}
      {mostrarConfirmar &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[80]">
            <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  Desconectar Mercado Libre
                </h3>
                <button
                  type="button"
                  onClick={() => !desconectando && setMostrarConfirmar(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <MercadoLibreIcon className="h-5 w-5" />
                </div>
                <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
                  ¿Desconectar <span className="font-medium text-neutral-800">{usuario}</span> de
                  esta marca? Tus publicaciones en Mercado Libre no cambian, y los productos que
                  trajiste quedan en Atlas. Vas a poder volver a conectarla cuando quieras.
                </p>
                {error && <p className="mt-3 text-[13px] text-red-600 text-center">{error}</p>}
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(false)}
                  disabled={desconectando}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmarDesconectar}
                  disabled={desconectando}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-neutral-900 hover:bg-neutral-800 text-white disabled:opacity-60"
                >
                  {desconectando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {desconectando ? "Desconectando…" : "Desconectar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default MercadoLibreStatus;
